import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Sparkles, ArrowUpRight, Mail, Truck } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setError("Missing session id");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setStatus(data);
      if (data.payment_status === "paid") {
        if (refreshUser) await refreshUser();
        return;
      }
      if (attempts > 15) {
        setError("Still waiting for Stripe to confirm. Refresh in a moment.");
      }
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [sessionId, attempts, refreshUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await poll();
      if (!cancelled && status?.payment_status !== "paid" && !error && attempts <= 15) {
        setTimeout(() => {
          if (!cancelled) setAttempts((a) => a + 1);
        }, 2000);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poll, status?.payment_status, error, attempts]);

  const paid = status?.payment_status === "paid";
  const lookupKey = status?.lookup_key || "";
  const isPlus = lookupKey.startsWith("aura_plus");
  const isBasic = lookupKey.startsWith("aura_basic");
  const isLetter = lookupKey === "aura_letter_send";
  const planName = isPlus ? "Aura Plus" : isBasic ? "Aura Basic" : isLetter ? "your certified letter" : "your new plan";

  // Compute the access-window countdown for the success card.
  const proUntil = status?.pro_until;
  const accessDays = status?.access_days || 30;
  let endsOnLabel = null;
  let daysLeft = null;
  if (proUntil) {
    const endDate = new Date(proUntil);
    endsOnLabel = endDate.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const now = new Date();
    daysLeft = Math.max(
      0,
      Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-[#F5F3EE] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="grid-bg absolute inset-0 opacity-30" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#D5FF3F]/12 blur-[120px] pointer-events-none" />
      <div className="relative card-surface card-elevate p-10 max-w-lg w-full text-center" data-testid="payment-success-card">
        {error ? (
          <>
            <div className="font-display text-3xl text-[#FF6B4A] mb-3">Hmm.</div>
            <p className="text-[#A8ADA5] mb-6">{error}</p>
            <Button onClick={() => navigate("/app/pricing")} className="rounded-full btn-outline">
              Back to pricing
            </Button>
          </>
        ) : paid ? (
          <>
            <div className="w-16 h-16 rounded-full bg-[#D5FF3F] flex items-center justify-center mx-auto mb-6">
              {isLetter ? (
                <Mail size={30} className="text-[#0B0C0E]" strokeWidth={2.5} />
              ) : (
                <CheckCircle2 size={30} className="text-[#0B0C0E]" strokeWidth={2.5} />
              )}
            </div>
            <div className="overline mb-3">
              {isLetter ? "Certified mail queued" : "Payment complete"}
            </div>
            <h1 className="font-display text-4xl leading-none mb-4">
              {isLetter ? (
                <>Your letter is on its way.</>
              ) : (
                <>Welcome to {planName}.</>
              )}
            </h1>
            <p className="text-[#A8ADA5] mb-8 max-w-sm mx-auto leading-relaxed">
              {isLetter
                ? "Aura just paid USPS and handed your letter to Lob's certified-mail printer. USPS Certified Mail with Return Receipt. Tracking numbers appear in your Mail dashboard within 60 seconds."
                : isPlus
                ? "Everything unlocked. Concierge review, mortgage sim, CFPB escalation, ID-theft kit. Let's clean this up."
                : isBasic
                ? "Unlimited scans, unlimited letters, cert-mail helper, all six strategies. Time to get to work."
                : "Your purchase is confirmed."}
            </p>
            {isLetter && (
              <div
                className="mb-8 mx-auto max-w-sm rounded-2xl border border-[#7CFF9B]/30 bg-[#7CFF9B]/8 p-5 text-left"
                data-testid="success-letter-info"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#7CFF9B]">
                    USPS Certified Mail
                  </div>
                  <Truck size={14} className="text-[#7CFF9B]" />
                </div>
                <div className="text-sm text-[#F5F3EE] mb-1">
                  Tracking number posts to your Mail dashboard shortly.
                </div>
                <div className="text-xs text-[#A8ADA5]">
                  Real USPS delivery confirmation. Return receipt included.
                </div>
              </div>
            )}
            {(isBasic || isPlus) && endsOnLabel && (
              <div
                className="mb-8 mx-auto max-w-sm rounded-2xl border border-[#D5FF3F]/25 bg-[#D5FF3F]/8 p-5 text-left"
                data-testid="success-countdown"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                    Membership active
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
                    Renews monthly
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="ticker text-4xl text-[#F5F3EE]" data-testid="success-days-left">
                    {daysLeft}
                  </span>
                  <span className="text-sm text-[#A8ADA5]">
                    day{daysLeft === 1 ? "" : "s"} until next bill
                  </span>
                </div>
                <div className="text-xs text-[#A8ADA5]">
                  Next bill: <span className="text-[#F5F3EE] font-medium">{endsOnLabel}</span>. Cancel anytime from billing.
                </div>
              </div>
            )}
            <Button
              onClick={() => navigate(isLetter ? "/app/mail" : "/app")}
              data-testid="success-goto-dashboard"
              className="rounded-full btn-lime h-12 px-7"
            >
              {isLetter ? (
                <>See tracking <ArrowUpRight size={16} className="ml-1.5" /></>
              ) : (
                <>Go to dashboard <ArrowUpRight size={16} className="ml-1.5" /></>
              )}
            </Button>
            {(isBasic || isPlus) && (
              <p className="mt-6 text-xs text-[#6D716C] leading-relaxed">
                You have <span className="text-[#D5FF3F]">3 days</span> to cancel for a full refund under your
                CROA right of rescission. Manage anytime in <Link to="/app/billing" className="underline">Billing</Link>.
              </p>
            )}
          </>
        ) : (
          <>
            <Sparkles size={30} className="text-[#D5FF3F] mx-auto mb-6 animate-pulse" />
            <div className="font-display text-3xl">Confirming payment…</div>
            <p className="text-[#A8ADA5] mt-3 text-sm">
              This usually takes a couple of seconds.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
